/**
 * ConsultoraModal — modal "Fale com uma de nossas consultoras".
 *
 * Used in 2 places (HomePage strip + TryOnEntryPage subtle link), single
 * source of truth — same look, same WhatsApp redirect.
 *
 * Behaviour:
 *   1. Visitor fills 3 fields:
 *      - name
 *      - 1+ weekdays (multi-select chips)
 *      - 1–3 time intervals (start/end HH:MM)
 *   2. Click on "Iniciar conversa" builds the pre-filled message and opens
 *      https://wa.me/<number>?text=<encoded> in a new tab — no backend
 *      persistence; this is intentionally a low-friction chat handoff, not a
 *      CRM lead. (wa.me may itself 302 to api.whatsapp.com on some devices —
 *      that's WhatsApp's own normalisation, not something we control. The
 *      new tab opens directly on the wa.me URL.)
 *   3. Phone number is read from /api/settings/consultoras-whatsapp (admin-
 *      editable, env var fallback).
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { Plus, X } from "lucide-react";

// Weekday list with the plural form we want to inject into the WhatsApp
// message ("nas segundas e quartas"). The `id` is what we store; the
// `plural` is the natural-language form used at message-build time.
const WEEKDAYS = [
  { id: "Segunda", plural: "segundas" },
  { id: "Terça", plural: "terças" },
  { id: "Quarta", plural: "quartas" },
  { id: "Quinta", plural: "quintas" },
  { id: "Sexta", plural: "sextas" },
  { id: "Sábado", plural: "sábados" },
  { id: "Domingo", plural: "domingos" },
];

const MAX_INTERVALS = 3;

/** "das 09:00 às 12:00" — single interval string. */
const fmtInterval = (iv) => `das ${iv.start} às ${iv.end}`;

/** Join an array of strings with PT-BR list connectors ("a", "a e b", "a, b e c"). */
function joinPt(items) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} e ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;
}

/** "nas segundas e quartas" — selected days as a single PT-BR phrase. */
function formatDays(days) {
  const plurals = days
    .map((d) => WEEKDAYS.find((w) => w.id === d)?.plural)
    .filter(Boolean);
  if (!plurals.length) return "";
  return `nas ${joinPt(plurals)}`;
}

/** "das 09:00 às 12:00 e das 18:00 às 20:00" — all valid intervals joined. */
function formatIntervals(intervals) {
  const valid = intervals.filter((iv) => iv.start && iv.end);
  return joinPt(valid.map(fmtInterval));
}

export default function ConsultoraModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    days: [],
    intervals: [{ start: "", end: "" }],
  });
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch the configured WhatsApp number once when the modal becomes visible.
  // Cached for the rest of the mount — if the admin updates it, the next page
  // load picks up the new value.
  useEffect(() => {
    if (!open || phone) return;
    api.get("/settings/consultoras-whatsapp").then((r) => setPhone(r?.data?.number || "")).catch(() => {});
  }, [open, phone]);

  const reset = () => {
    setForm({ name: "", days: [], intervals: [{ start: "", end: "" }] });
    setError("");
  };

  const toggleDay = (id) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(id) ? f.days.filter((d) => d !== id) : [...f.days, id],
    }));
  };

  const addInterval = () => {
    setForm((f) => (
      f.intervals.length >= MAX_INTERVALS ? f : { ...f, intervals: [...f.intervals, { start: "", end: "" }] }
    ));
  };

  const removeInterval = (idx) => {
    setForm((f) => ({ ...f, intervals: f.intervals.filter((_, i) => i !== idx) }));
  };

  const updateInterval = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      intervals: f.intervals.map((iv, i) => (i === idx ? { ...iv, [field]: value } : iv)),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    const name = form.name.trim();
    if (!name) { setError("Por favor, informe seu nome."); return; }
    if (form.days.length === 0) { setError("Selecione ao menos um dia da semana."); return; }
    const validIntervals = form.intervals.filter((iv) => iv.start && iv.end);
    if (validIntervals.length === 0) {
      setError("Informe ao menos um intervalo de horário (início e fim).");
      return;
    }
    // Reject reversed intervals so the message stays coherent.
    const reversed = validIntervals.find((iv) => iv.end <= iv.start);
    if (reversed) {
      setError(`Horário inválido: ${fmtInterval(reversed)}. O fim deve ser depois do início.`);
      return;
    }
    if (!phone) {
      setError("Estamos com instabilidade no canal de atendimento. Tente novamente em alguns minutos.");
      return;
    }
    setLoading(true);
    const daysStr = formatDays(form.days);
    const intervalsStr = formatIntervals(validIntervals);
    const text = `Olá! Me chamo ${name}. Gostaria de agendar uma consulta personalizada com uma das consultoras da Prize Joias. Estou disponível ${daysStr}, ${intervalsStr}. Aguardo o retorno! 💎`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Close + reset right after we open WA so the visitor returns to a clean
    // form if they come back to the modal later.
    onOpenChange(false);
    reset();
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
    >
      <DialogContent
        className="max-w-md bg-[#F7F4ED] p-0 gap-0 border-[#E8E4DF] max-h-[92vh] overflow-y-auto"
        data-testid="consultora-modal"
      >
        <DialogHeader className="px-7 pt-8 pb-2">
          <DialogTitle className="font-display text-2xl md:text-3xl text-[#1A1A1A] tracking-tight">
            Fale com uma de nossas consultoras
          </DialogTitle>
          <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
            Em poucos minutos retornamos pelo WhatsApp para te ajudar a escolher a peça perfeita.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-7 pb-7 pt-5 space-y-5" data-testid="consultora-form">
          <Field label="Seu nome">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
              required
              maxLength={80}
              className="w-full bg-white border border-[#E8E4DF] px-4 py-3 text-sm focus:border-[#B8A88A] outline-none"
              data-testid="consultora-name-input"
              placeholder="Como prefere ser chamada"
            />
          </Field>

          <Field label="Melhor dia para contato">
            <div className="flex flex-wrap gap-2" data-testid="consultora-days-group">
              {WEEKDAYS.map((d) => {
                const active = form.days.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDay(d.id)}
                    data-testid={`consultora-day-${d.id}`}
                    data-active={active}
                    aria-pressed={active}
                    className={
                      "text-xs uppercase tracking-[0.14em] px-3.5 py-2 border transition-colors " +
                      (active
                        ? "bg-[#B8A88A] border-[#B8A88A] text-white"
                        : "bg-white border-[#E8E4DF] text-[#6B6B6B] hover:border-[#B8A88A] hover:text-[#1A1A1A]")
                    }
                  >
                    {d.id}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#A8A8A8] mt-2 italic">Selecione um ou mais dias.</p>
          </Field>

          <Field label="Melhor horário para contato">
            <div className="space-y-2" data-testid="consultora-intervals-group">
              {form.intervals.map((iv, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`consultora-interval-${idx}`}>
                  <span className="text-xs text-[#6B6B6B] w-8">das</span>
                  <input
                    type="time"
                    value={iv.start}
                    onChange={(e) => updateInterval(idx, "start", e.target.value)}
                    className="flex-1 bg-white border border-[#E8E4DF] px-3 py-2.5 text-sm focus:border-[#B8A88A] outline-none"
                    data-testid={`consultora-interval-start-${idx}`}
                  />
                  <span className="text-xs text-[#6B6B6B]">às</span>
                  <input
                    type="time"
                    value={iv.end}
                    onChange={(e) => updateInterval(idx, "end", e.target.value)}
                    className="flex-1 bg-white border border-[#E8E4DF] px-3 py-2.5 text-sm focus:border-[#B8A88A] outline-none"
                    data-testid={`consultora-interval-end-${idx}`}
                  />
                  {form.intervals.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInterval(idx)}
                      aria-label="Remover intervalo"
                      className="text-[#A8A8A8] hover:text-[#B85050] p-1.5"
                      data-testid={`consultora-remove-interval-${idx}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {form.intervals.length < MAX_INTERVALS && (
              <button
                type="button"
                onClick={addInterval}
                className="mt-3 text-xs uppercase tracking-[0.14em] text-[#B8A88A] hover:text-[#8C7B5E] inline-flex items-center gap-1.5"
                data-testid="consultora-add-interval"
              >
                <Plus size={13} /> Adicionar outro horário
              </button>
            )}
          </Field>

          {error && (
            <p className="text-sm text-[#B85050]" data-testid="consultora-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#B8A88A] hover:bg-[#9F8F70] text-white text-sm uppercase tracking-[0.18em] py-4 transition-colors disabled:opacity-50"
            data-testid="consultora-submit-btn"
          >
            {loading ? "Abrindo WhatsApp…" : "Iniciar conversa"}
          </button>

          <p className="text-xs text-[#A8A8A8] text-center italic pt-1">
            Você será redirecionada para o WhatsApp com a mensagem pronta.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[#A8A8A8] block mb-2">{label}</span>
      {children}
    </label>
  );
}
